import type { PackagingShareWrite } from "./useUpsertPackaging";
import { useMutation, useQueryClient } from "react-query";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { productGridKey } from "../products/useGetAllproduct";
import { getProductScoresKey } from "../olapScores/useGetProductScores";
import { packagingCompositesGridKey, packagingElementsGridKey, packagingGridKey } from "./useGetAllPackagings";

/**
 * Wraps org-scoped create and item update for tenant packaging elements.
 *
 * CREATE: `POST /api/organizations/{organizationId}/packaging-elements`
 * UPDATE: `PUT /api/packaging-elements/{id}` (resource-anchored authorization)
 */
export type PackagingElementWrite = {
    id?: number;
    name: string;
    externalId?: string | null;
    weight: number;
    packagingReferenceId: number;
    recycled?: boolean;
    recycledProportion?: number | null;
    recyclable?: boolean | null;
    reuse?: boolean;
    shares: PackagingShareWrite[];
    tags?: Record<string, string[]>;
};

type Options = {
    organizationId: number;
    campaignId: number;
    packaging: PackagingElementWrite;
};

export const useUpsertPackagingElement = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (options: Options) => {
            const { organizationId, campaignId, packaging } = options;
            const response =
                packaging.id === undefined ?
                    await loggedInApiCall.post(`/api/organizations/${organizationId}/packaging-elements`, packaging, {
                        params: { campaign: campaignId },
                    })
                :   await loggedInApiCall.put(`/api/packaging-elements/${packaging.id}`, packaging, {
                        params: { campaign: campaignId, organization_id: organizationId },
                    });

            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries([packagingGridKey]);
            queryClient.invalidateQueries([packagingElementsGridKey]);
            queryClient.invalidateQueries([packagingCompositesGridKey]);
            queryClient.invalidateQueries([productGridKey]);
            queryClient.invalidateQueries([getProductScoresKey]);
        },
    });
};
